import useSWR, { SWRResponse } from 'swr'
import dayjs, { OpUnitType } from 'dayjs'
import { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import NewDashboardComponent from './component'
import axiosInstance from '../../../utils/axiosInstance'

interface Logo {
  __typename: string
  url: string
}

export interface Customer {
  __typename?: string
  id?: string
  name?: string
  type?: string
  logo?: Logo
}

export interface OrdersProps {
  monthlyTurnOver: number
  yearlyTurnOver: number
  activeOrders: IOrder[]
  monthlyOrders: IOrder[]
  yearlyOrders: IOrder[]
  openOrders: IOrder[]
  orders: IOrder[]
  todoOrders: IOrder[]
  inProgressOrders: IOrder[]
  waitingForApprovalOrders: IOrder[]
  approvedOrders: IOrder[]
  materialCompleteOrders: IOrder[]
  size: number
}

export interface IOrder {
  id: string
  customer: Customer
  price: string
  jobType: string
  jobName: string
  channel: string[]
  budget: string
  runtime: number
  createdAt: string
}

const NewDashboard = () => {
  const [customersResellersTab, setCustomersResellersTab] = useState<'all' | 'direct' | 'reseller'>(
    'all'
  )

  const params = { rowsPerPage: 1000 }
  const {
    data: fetchedData,
    mutate,
    isLoading,
  }: SWRResponse = useSWR('/orders', (url) => axiosInstance.get(url, { params }))
  const data = fetchedData?.data?.orders

  // Fetch admin dashboard data
  const { data: dashboardData, isLoading: isDashboardLoading }: SWRResponse = useSWR(
    `/admin-dashboard?customerType=${customersResellersTab}&limit=10`,
    (url: string) => axiosInstance.get(url)
  )
  const [formatData, setFormatData] = useState<OrdersProps>({
    monthlyTurnOver: 0,
    yearlyTurnOver: 0,
    activeOrders: [],
    monthlyOrders: [],
    yearlyOrders: [],
    openOrders: [],
    todoOrders: [],
    inProgressOrders: [],
    materialCompleteOrders: [],
    approvedOrders: [],
    waitingForApprovalOrders: [],
    orders: [],
    size: 0,
  })

  const formattedCustomersResellers = useMemo(() => {
    if (!dashboardData?.data?.customersResellers?.customers) return []
    return dashboardData.data.customersResellers.customers
  }, [dashboardData])

  const customersResellersTotal = useMemo(() => {
    if (!dashboardData?.data?.customersResellers?.total) {
      return { revenue: 0, numberOfOrders: 0 }
    }
    return {
      revenue: dashboardData.data.customersResellers.total.revenue,
      numberOfOrders: dashboardData.data.customersResellers.total.numberOfOrders,
    }
  }, [dashboardData])

  const { triggerOrdersRefetch } = useSelector((state: any) => state.general)

  const sumPrice = (data: IOrder[]) => {
    return data.reduce((accumulator, currentValue) => {
      const price = currentValue.price ? parseInt(currentValue.price, 10) : 0
      return accumulator + price
    }, 0)
  }

  const handleChangeCustomersResellersTab = (tab: 'all' | 'direct' | 'reseller') => {
    setCustomersResellersTab(tab)
  }

  const filterDate = (items: any[], filterBy: OpUnitType | undefined) => {
    const currentDate = dayjs()
    const isItems = items && items.length

    const filteredItems = isItems
      ? items.filter((item) => {
          const itemDate = dayjs(item?.createdAt)
          return itemDate.isSame(currentDate, filterBy)
        })
      : []

    return filteredItems
  }

  useEffect(() => {
    if (!data) return

    const monthlyOrders = filterDate(data, 'month') || []
    const yearlyOrders = filterDate(data, 'year') || []

    const yearlyTurnOver = sumPrice(yearlyOrders)
    const monthlyTurnOver = sumPrice(monthlyOrders)
    const openOrders =
      monthlyOrders.filter(({ status }) =>
        ['inProgress', 'todo', 'materialComplete', 'waitingForApproval'].includes(status)
      ) || []

    const todoOrders = data.filter(({ status }) => status === 'todo') || []
    const active = data.filter(({ status }) => status === 'active') || []
    const activeOrders = data.filter(({ status }) => status === 'active') || []
    const inProgressOrders = data.filter(({ status }) => status === 'inProgress') || []
    const approvedOrders = data.filter(({ status }) => status === 'approved') || []
    const waitingForApprovalOrders =
      data.filter(({ status }) => status === 'waitingForApproval') || []
    const materialCompleteOrders = data.filter(({ status }) => status === 'materialComplete') || []
    const orders = data || []

    setFormatData((prev) => ({
      ...prev,
      active,
      monthlyOrders,
      yearlyOrders,
      monthlyTurnOver,
      yearlyTurnOver,
      openOrders,
      activeOrders,
      todoOrders,
      materialCompleteOrders,
      approvedOrders,
      waitingForApprovalOrders,
      inProgressOrders,
      orders,
      size: data.length,
    }))
  }, [data])

  useEffect(() => {
    if (triggerOrdersRefetch) mutate()
  }, [triggerOrdersRefetch, mutate])

  return (
    <NewDashboardComponent
      isLoading={isLoading || isDashboardLoading}
      customersResellersTab={customersResellersTab}
      formatData={formatData}
      customersResellers={formattedCustomersResellers}
      customersResellersTotal={customersResellersTotal}
      onChangeCustomersResellersTab={handleChangeCustomersResellersTab}
      dashboardStats={dashboardData?.data?.stats}
    />
  )
}

export default NewDashboard
